import React, { useState, useEffect } from 'react';
import { submitArticleFeedback } from '@/services/feedback';

interface FeedbackFormProps {
  articleSlug: string;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ articleSlug }) => {
  const [formData, setFormData] = useState({
    rating: 0,
    comment: '',
    email: '',  // Optional email for follow-up
  });
  const [honeypot, setHoneypot] = useState(''); // Honeypot field to catch bots
  const [formRenderedAt, setFormRenderedAt] = useState(Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<{
    success?: boolean;
    message?: string;
  } | null>(null);

  // Set the initial time when the component is first rendered
  useEffect(() => {
    setFormRenderedAt(Date.now());
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Anti-spam measures
    const timeElapsed = Date.now() - formRenderedAt;
    const tooFast = timeElapsed < 3000; // Less than 3 seconds is suspicious
    
    // Check honeypot - if filled, it's likely a bot
    if (honeypot) {
      // Pretend success but don't actually submit
      setSubmitResult({
        success: true,
        message: 'Thank you for your feedback!',
      });
      return;
    }
    
    // Too fast is suspicious
    if (tooFast) {
      setSubmitResult({
        success: false,
        message: 'Please take a moment to consider your feedback before submitting.',
      });
      return;
    }
    
    setSubmitting(true);
    setSubmitResult(null);

    try {
      // Save feedback using our service
      const result = await submitArticleFeedback({
        article_slug: articleSlug,
        rating: formData.rating,
        comment: formData.comment || undefined,
        email: formData.email || undefined
      });
      
      if (result.success) {
        setSubmitResult({
          success: true,
          message: 'Thank you for your feedback!',
        });
        
        // Reset form after successful submission
        setFormData({
          rating: 0,
          comment: '',
          email: '',
        });
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitResult({
        success: false,
        message: 'There was an error submitting your feedback. Please try again later.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFormData((prev) => ({
      ...prev,
      rating,
    }));
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg mt-8 border border-gray-200">
      <h3 className="text-xl font-semibold text-gray-900 mb-4">Was this article helpful?</h3>
      
      {submitResult ? (
        <div className={`p-4 rounded-md ${submitResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {submitResult.message}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Rating stars */}
          <div>
            <div className="flex items-center space-x-1 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingChange(star)}
                  className={`text-2xl focus:outline-none ${
                    formData.rating >= star ? 'text-yellow-400' : 'text-gray-300'
                  }`}
                  aria-label={`Rate ${star} stars`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          
          {/* Comment field */}
          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-1">
              Your feedback (optional)
            </label>
            <textarea
              id="comment"
              name="comment"
              value={formData.comment}
              onChange={handleChange}
              rows={3}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              disabled={submitting}
            />
          </div>
          
          {/* Email field (optional) */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Your email (optional)
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              disabled={submitting}
              placeholder="For follow-up questions"
            />
          </div>
          
          {/* Honeypot field - hidden from real users but bots will fill it */}
          <div className="hidden" aria-hidden="true">
            <label htmlFor="website">Website (Leave this empty)</label>
            <input
              type="text"
              id="website"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>
          
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            disabled={submitting || formData.rating === 0}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      )}
    </div>
  );
};

export default FeedbackForm; 